/**
 * Textes de la page de connexion (ADR-091, habillage v1 d'ADR-092) en trois langues. Le sélecteur
 * affiche « en / fr / sp » comme au v1, mais le code interne de l'espagnol est `es` (BCP-47) : `sp`
 * n'est qu'une étiquette. Le français fait foi ; l'anglais et l'espagnol sont des traductions.
 */

export type SignInLocale = 'fr' | 'en' | 'es';

export const SIGNIN_LOCALES: ReadonlyArray<{ code: SignInLocale; label: string; name: string }> = [
  { code: 'en', label: 'en', name: 'English' },
  { code: 'fr', label: 'fr', name: 'Français' },
  { code: 'es', label: 'sp', name: 'Español' },
];

export interface SignInTexts {
  title: string;
  description: string;
  email: string;
  password: string;
  remember: string;
  clearEmail: string;
  showPassword: string;
  hidePassword: string;
  emailPlaceholder: string;
  passwordPlaceholder: string;
  signingIn: string;
  verifying: string;
  sending: string;
  forgot: string;
  submit: string;
  noAccount: string;
  booking: string;
  language: string;
  loginFailed: string;
  rateLimited: string;
  /** « {n} » : minutes avant la prochaine tentative (en-tête Retry-After). */
  rateLimitedIn: string;
  unavailable: string;
  codeInvalid: string;
  details: string;
  challengeExpired: string;
  mfaLocked: string;
  unexpected: string;
  totpTitle: string;
  totpHelp: string;
  code: string;
  verify: string;
  back: string;
  enrollTitle: string;
  enrollHelp: string;
  showKey: string;
  keyLabel: string;
  openLink: string;
  addAccount: string;
  shownCode: string;
  activate: string;
  recoveryTitle: string;
  recoveryHelp: string;
  recoveryKept: string;
  forgotTitle: string;
  forgotSent: string;
  getLink: string;
}

const fr: SignInTexts = {
  title: 'Connectez-vous pour continuer.',
  description: 'Saisissez votre adresse e-mail et votre mot de passe.',
  email: 'Adresse e-mail',
  password: 'Mot de passe',
  remember: 'Se souvenir de moi',
  clearEmail: 'Effacer l’adresse',
  showPassword: 'Afficher le mot de passe',
  hidePassword: 'Masquer le mot de passe',
  emailPlaceholder: 'nom@exemple.fr',
  passwordPlaceholder: 'Votre mot de passe',
  signingIn: 'Connexion…',
  verifying: 'Vérification…',
  sending: 'Envoi…',
  forgot: 'Mot de passe oublié ?',
  submit: 'Se connecter',
  noAccount: 'Pas encore client ?',
  booking: 'Prendre rendez-vous',
  language: 'Langue',
  loginFailed: 'Adresse ou mot de passe incorrect.',
  rateLimited: 'Trop de tentatives. Réessayez dans quelques minutes.',
  rateLimitedIn: 'Trop de tentatives, réessayez dans {n} minute(s).',
  unavailable: 'Service momentanément indisponible. Réessayez dans un instant.',
  codeInvalid: 'Code incorrect.',
  details: 'Détail technique',
  challengeExpired: 'La vérification a expiré. Reconnectez-vous.',
  mfaLocked: 'Trop de codes incorrects. Réessayez dans quelques minutes.',
  unexpected: 'Une erreur est survenue.',
  totpTitle: 'Vérification en deux étapes',
  totpHelp: 'Saisissez le code à six chiffres de votre application d’authentification, ou un code de récupération.',
  code: 'Code',
  verify: 'Vérifier',
  back: 'Revenir à la connexion',
  enrollTitle: 'Activez la vérification en deux étapes',
  enrollHelp: 'Votre rôle exige un second facteur. Ajoutez AYUMIA dans une application d’authentification (TOTP), puis saisissez le code affiché.',
  showKey: 'Afficher la clé',
  keyLabel: 'Clé à saisir dans l’application :',
  openLink: 'Ou ouvrez ce lien sur le téléphone :',
  addAccount: 'ajouter le compte',
  shownCode: 'Code affiché',
  activate: 'Activer',
  recoveryTitle: 'Codes de récupération',
  recoveryHelp: 'Chacun de ces codes remplace une fois le code de l’application, si vous perdez votre téléphone. Conservez-les hors de l’ordinateur : ils ne seront plus affichés.',
  recoveryKept: 'J’ai conservé mes codes',
  forgotTitle: 'Mot de passe oublié',
  forgotSent: 'Si cette adresse correspond à un compte, un lien de réinitialisation vient de lui être envoyé. Il est valable 30 minutes.',
  getLink: 'Recevoir un lien',
};

const en: SignInTexts = {
  title: 'Sign in to continue.',
  description: 'Enter your email address and your password.',
  email: 'Email address',
  password: 'Password',
  remember: 'Remember me',
  clearEmail: 'Clear the address',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  emailPlaceholder: 'name@example.com',
  passwordPlaceholder: 'Your password',
  signingIn: 'Signing in…',
  verifying: 'Verifying…',
  sending: 'Sending…',
  forgot: 'Forgot your password?',
  submit: 'Sign in',
  noAccount: 'Not a customer yet?',
  booking: 'Book a meeting',
  language: 'Language',
  loginFailed: 'Incorrect email address or password.',
  rateLimited: 'Too many attempts. Try again in a few minutes.',
  rateLimitedIn: 'Too many attempts, try again in {n} minute(s).',
  unavailable: 'Service temporarily unavailable. Try again in a moment.',
  codeInvalid: 'Incorrect code.',
  details: 'Technical detail',
  challengeExpired: 'The verification has expired. Sign in again.',
  mfaLocked: 'Too many incorrect codes. Try again in a few minutes.',
  unexpected: 'Something went wrong.',
  totpTitle: 'Two-step verification',
  totpHelp: 'Enter the six-digit code from your authenticator app, or a recovery code.',
  code: 'Code',
  verify: 'Verify',
  back: 'Back to sign in',
  enrollTitle: 'Turn on two-step verification',
  enrollHelp: 'Your role requires a second factor. Add AYUMIA to an authenticator app (TOTP), then enter the code it shows.',
  showKey: 'Show the key',
  keyLabel: 'Key to enter in the app:',
  openLink: 'Or open this link on your phone:',
  addAccount: 'add the account',
  shownCode: 'Code shown',
  activate: 'Turn on',
  recoveryTitle: 'Recovery codes',
  recoveryHelp: 'Each code replaces the app code once, if you lose your phone. Keep them away from this computer: they will not be shown again.',
  recoveryKept: 'I have kept my codes',
  forgotTitle: 'Forgot your password',
  forgotSent: 'If this address matches an account, a reset link has just been sent to it. It is valid for 30 minutes.',
  getLink: 'Get a link',
};

// À RELIRE : traductions espagnoles non validées par un locuteur.
const es: SignInTexts = {
  title: 'Inicie sesión para continuar.',
  description: 'Introduzca su dirección de correo electrónico y su contraseña.',
  email: 'Correo electrónico',
  password: 'Contraseña',
  remember: 'Recordarme',
  clearEmail: 'Borrar la dirección',
  showPassword: 'Mostrar la contraseña',
  hidePassword: 'Ocultar la contraseña',
  emailPlaceholder: 'nombre@ejemplo.es',
  passwordPlaceholder: 'Su contraseña',
  signingIn: 'Iniciando sesión…',
  verifying: 'Verificando…',
  sending: 'Enviando…',
  forgot: '¿Ha olvidado su contraseña?',
  submit: 'Iniciar sesión',
  noAccount: '¿Todavía no es cliente?',
  booking: 'Pedir una cita',
  language: 'Idioma',
  loginFailed: 'Correo electrónico o contraseña incorrectos.',
  rateLimited: 'Demasiados intentos. Vuelva a intentarlo en unos minutos.',
  rateLimitedIn: 'Demasiados intentos, vuelva a intentarlo en {n} minuto(s).',
  unavailable: 'Servicio no disponible temporalmente. Vuelva a intentarlo en un momento.',
  codeInvalid: 'Código incorrecto.',
  details: 'Detalle técnico',
  challengeExpired: 'La verificación ha caducado. Vuelva a iniciar sesión.',
  mfaLocked: 'Demasiados códigos incorrectos. Vuelva a intentarlo en unos minutos.',
  unexpected: 'Se ha producido un error.',
  totpTitle: 'Verificación en dos pasos',
  totpHelp: 'Introduzca el código de seis cifras de su aplicación de autenticación, o un código de recuperación.',
  code: 'Código',
  verify: 'Verificar',
  back: 'Volver al inicio de sesión',
  enrollTitle: 'Active la verificación en dos pasos',
  enrollHelp: 'Su rol exige un segundo factor. Añada AYUMIA a una aplicación de autenticación (TOTP) e introduzca el código que muestra.',
  showKey: 'Mostrar la clave',
  keyLabel: 'Clave que debe introducir en la aplicación:',
  openLink: 'O abra este enlace en el teléfono:',
  addAccount: 'añadir la cuenta',
  shownCode: 'Código mostrado',
  activate: 'Activar',
  recoveryTitle: 'Códigos de recuperación',
  recoveryHelp: 'Cada código sustituye una vez al código de la aplicación si pierde el teléfono. Guárdelos fuera de este ordenador: no se volverán a mostrar.',
  recoveryKept: 'He guardado mis códigos',
  forgotTitle: 'Contraseña olvidada',
  forgotSent: 'Si esta dirección corresponde a una cuenta, se le acaba de enviar un enlace de restablecimiento. Es válido durante 30 minutos.',
  getLink: 'Recibir un enlace',
};

export const SIGNIN_TEXTS: Readonly<Record<SignInLocale, SignInTexts>> = { fr, en, es };
